import React, { useState, useEffect } from "react";
import "../estilos_login.css";
import config from "../config.json";

import { useGoogleLogin } from "@react-oauth/google";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { initFacebookSDK } from "../context/FacebookInit";

const Login: React.FC = () => {
  const { login } = useAuth();

  const [step, setStep] = useState<
    "login" | "emailInput" | "verifyCode"
  >("login");

  const [email, setEmail]                     = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [isLoading, setIsLoading]             = useState(false);
  const [aceptado, setAceptado]               = useState(false);
  const [errorMsg, setErrorMsg]               = useState("");
  const [successMsg, setSuccessMsg]           = useState("");

  /* =========================
     GOOGLE LOGIN
  ========================== */
  const loginWithGoogle = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        const googleUser = await axios.get(
          "https://www.googleapis.com/oauth2/v3/userinfo",
          { headers: { Authorization: `Bearer ${tokenResponse.access_token}` } }
        );

        const res = await axios.post(
          config.ENDPOINTS.LOGIN_GOOGLE,
          {
            email:    googleUser.data.email,
            name:     googleUser.data.name,
            googleId: googleUser.data.sub,
            picture:  googleUser.data.picture,
          }
        );

        if (res.data.session_uuid) {
          localStorage.setItem("sessionUUID", res.data.session_uuid);
        }

        // Guardar info de membresía
        localStorage.setItem("idmember",  String(res.data.user?.idmember  ?? 0));
        localStorage.setItem("esMiembro", String(res.data.user?.esMiembro ?? false));

        login({ ...res.data.user, avatar: googleUser.data.picture }, res.data.token);

        closeModal();
      } catch (error) {
        console.error("Error login Google:", error);
        setErrorMsg("Error al iniciar sesión con Google");
      }
    },
    onError: () => setErrorMsg("Inicio de sesión con Google cancelado"),
  });

  /* =========================
     FACEBOOK LOGIN
  ========================== */
  useEffect(() => {
    initFacebookSDK()
      .catch((err) => console.warn("Facebook SDK no cargó:", err));
  }, []);

  const loginWithFacebook = () => {
    const FB = (window as any).FB;
    if (!FB) { setErrorMsg("Facebook SDK no cargado"); return; }

    FB.login(
      (response: any) => {
        if (!response.authResponse) {
          setErrorMsg("Inicio de sesión con Facebook cancelado");
          return;
        }

        FB.api("/me", { fields: "id,name,picture" }, async (user: any) => {
          try {
            if (!user || !user.id) throw new Error("No se recibió información de Facebook");

            const picture = user.picture?.data?.url || "";
            const payload = {
              facebookId: user.id,
              name:       user.name || "Usuario Facebook",
              email:      `fb_${user.id}@facebook.com`,
              picture,
            };

            const res = await axios.post(
              config.ENDPOINTS.LOGIN_FACEBOOK,
              payload,
              { headers: { "Content-Type": "application/json" } }
            );

            if (!res.data?.success) throw new Error(res.data?.message || "Error en el login");

            if (res.data.session_uuid) {
              localStorage.setItem("sessionUUID", res.data.session_uuid);
            }

            localStorage.setItem("idmember",  String(res.data.user?.idmember  ?? 0));
            localStorage.setItem("esMiembro", String(res.data.user?.esMiembro ?? false));

            login({ ...res.data.user, avatar: picture }, res.data.token);
            closeModal();

          } catch (error: any) {
            setErrorMsg(error.response?.data?.message || error.message || "Error al iniciar sesión con Facebook");
          }
        });
      },
      { scope: "public_profile" }
    );
  };

  /* =========================
     EMAIL — ENVIAR OTP
     (o loguear directo si el email ya estaba verificado antes)
  ========================== */
  const handleSendOTP = async () => {
    setErrorMsg("");
    setSuccessMsg("");

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrorMsg("Ingresa un email válido");
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch(
        config.ENDPOINTS.REGISTRO,
        {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({ email }),
        }
      );
      const data = await res.json();

      // FIX: el backend (registro.php) manda "alreadyVerified", no
      // "isverified" — con el nombre incorrecto, un email NUEVO caía
      // en el bloque de "ya verificado" y se llamaba a login() con
      // data.user/data.token vacíos (undefined), dejando pasar al
      // usuario sin verificación real y sin mostrarle el paso del OTP.
      if (!data.success) {
        setErrorMsg(data.message || "Error al enviar el código");
        return;
      }

      if (data.alreadyVerified) {
        // Email ya verificado antes: login directo, sin pedir OTP.
        localStorage.setItem("idmember",  String(data.ismember  ?? 0));
        localStorage.setItem("esMiembro", String(data.esMiembro ?? false));

        login(data.user, data.token);
        closeModal();
      } else {
        // Email nuevo o no verificado: se mandó el OTP, pedir el código.
        setSuccessMsg(`Código enviado a ${email}`);
        setStep("verifyCode");
      }
    } catch {
      setErrorMsg("Error de conexión. Intenta de nuevo.");
    } finally {
      setIsLoading(false);
    }
  };

  /* =========================
     EMAIL — VERIFICAR OTP
  ========================== */
  const handleVerifyCode = async () => {
    setErrorMsg("");
    setIsLoading(true);
    try {
      const res = await fetch(
        config.ENDPOINTS.VALIDAR_CODIGO,
        {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({ email, codigo: verificationCode }),
        }
      );
      const data = await res.json();

      if (data.success) {
        // Guardar membresía
        localStorage.setItem("idmember",  String(data.idmember  ?? 0));
        localStorage.setItem("esMiembro", String(data.esMiembro ?? false));

        login(data.user, data.token);
        closeModal();
      } else {
        setErrorMsg(data.message || "Código incorrecto");
      }
    } catch {
      setErrorMsg("Error de conexión. Intenta de nuevo.");
    } finally {
      setIsLoading(false);
    }
  };

  /* =========================
     HELPERS
  ========================== */
  const closeModal = () => {
    const modal = document.getElementById("loginModal");
    if (modal) (window as any).bootstrap.Modal.getInstance(modal)?.hide();
    // Reset
    setStep("login");
    setEmail("");
    setVerificationCode("");
    setErrorMsg("");
    setSuccessMsg("");
  };

  /* =========================
     RENDER
  ========================== */
  return (
    <div className="modal fade" id="loginModal" tabIndex={-1}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {step === "login"       && "Iniciar sesión"}
              {step === "emailInput"  && "Continuar con email"}
              {step === "verifyCode"  && "Verifica tu código"}
            </h5>
            <button type="button" className="btn-close" data-bs-dismiss="modal" onClick={closeModal} />
          </div>

          <div className="modal-body">

            {/* Mensajes */}
            {errorMsg   && <div className="alert alert-danger   py-2 mb-3" style={{ fontSize: 13 }}>{errorMsg}</div>}
            {successMsg && <div className="alert alert-success  py-2 mb-3" style={{ fontSize: 13 }}>{successMsg}</div>}

            {/* ── PASO 1: opciones de login ── */}
            {step === "login" && (
              <>
                <div className="form-check mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="aceptarPrivacidad"
                    checked={aceptado}
                    onChange={(e) => setAceptado(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="aceptarPrivacidad" style={{ fontSize: 13, color: "#555" }}>
                    Acepto la{" "}
                    <a href={config.PRIVACY_URL} target="_blank" rel="noopener noreferrer">
                      Política de Privacidad
                    </a>
                  </label>
                </div>

                <button
                  className="btn btn-social w-100 mb-2 d-flex align-items-center"
                  onClick={() => loginWithGoogle()}
                  disabled={!aceptado}
                >
                  <img src="/assets/google.svg" className="me-2" alt="Google" />
                  Continuar con Google
                </button>

                <button
                  className="btn btn-social w-100 mb-2 d-flex align-items-center"
                  onClick={loginWithFacebook}
                  disabled={!aceptado}
                >
                  <img src="/assets/facebook.svg" className="me-2" alt="Facebook" />
                  Continuar con Facebook
                </button>

                <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "12px 0" }}>
                  <hr style={{ flex: 1, margin: 0 }} />
                  <span style={{ fontSize: 12, color: "#94a3b8" }}>o</span>
                  <hr style={{ flex: 1, margin: 0 }} />
                </div>

                <button
                  className="btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center"
                  onClick={() => { setErrorMsg(""); setStep("emailInput"); }}
                  disabled={!aceptado}
                >
                  ✉️ &nbsp; Continuar con email
                </button>
              </>
            )}

            {/* ── PASO 2: ingresar email ── */}
            {step === "emailInput" && (
              <>
                <p style={{ fontSize: 13, color: "#64748b", marginBottom: 16 }}>
                  Te enviaremos un código de 6 dígitos para acceder.
                </p>
                <input
                  type="email"
                  className="form-control mb-3"
                  placeholder="tu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSendOTP()}
                  autoFocus
                />
                <button
                  className="btn btn-primary w-100"
                  onClick={handleSendOTP}
                  disabled={isLoading}
                >
                  {isLoading ? "Enviando..." : "Enviar código"}
                </button>
                <button
                  className="btn btn-link w-100 mt-2"
                  style={{ fontSize: 13 }}
                  onClick={() => { setStep("login"); setErrorMsg(""); }}
                >
                  ← Volver
                </button>
              </>
            )}

            {/* ── PASO 3: ingresar código OTP ── */}
            {step === "verifyCode" && (
              <>
                <p style={{ fontSize: 13, color: "#64748b", marginBottom: 4 }}>
                  Ingresa el código que enviamos a:
                </p>
                <p style={{ fontSize: 14, fontWeight: 600, color: "#1e293b", marginBottom: 16 }}>
                  {email}
                </p>
                <input
                  type="text"
                  className="form-control mb-3"
                  placeholder="000000"
                  maxLength={6}
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ""))}
                  onKeyDown={(e) => e.key === "Enter" && handleVerifyCode()}
                  style={{ fontSize: 24, letterSpacing: 8, textAlign: "center", fontWeight: 700 }}
                  autoFocus
                />
                <button
                  className="btn btn-primary w-100"
                  onClick={handleVerifyCode}
                  disabled={isLoading || verificationCode.length < 6}
                >
                  {isLoading ? "Verificando..." : "Acceder"}
                </button>
                <button
                  className="btn btn-link w-100 mt-2"
                  style={{ fontSize: 13 }}
                  onClick={() => { setStep("emailInput"); setVerificationCode(""); setErrorMsg(""); }}
                >
                  ← Cambiar email / Reenviar código
                </button>
              </>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;